import { Capacitor } from '@capacitor/core';
import { Config } from '@nusrif/types';

export interface AppUpdateInfo {
  currentVersion: string;
  latestVersion: string;
  minSupportedVersion: string;
  forceUpdate: boolean;
  hasUpdate: boolean;
  isForceUpdate: boolean;
}

export interface AppUpdateService {
  checkForUpdates: () => Promise<AppUpdateInfo>;
  openStore: () => Promise<void>;
}

function readConfigFromCache(): Config | null {
  try {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem('nusrif-config');
    if (!raw) return null;
    return JSON.parse(raw) as Config;
  } catch {
    return null;
  }
}

class NativeAppUpdateService implements AppUpdateService {
  async checkForUpdates(): Promise<AppUpdateInfo> {
    const config = readConfigFromCache();
    if (!config) throw new Error('Config not available');

    try {
      // On native, we cannot rely on vite's package version. Prefer App.getInfo if available via bridge.
      // Fallback to web mechanism if bridge fails.
      // We avoid importing @capacitor/app to keep this package lean; rely on version.json/web fallback below if needed.
      return this.checkForUpdatesWebFallback(config);
    } catch (error) {
      console.error('Failed native version check, falling back to web:', error);
      return this.checkForUpdatesWebFallback(config);
    }
  }

  private async checkForUpdatesWebFallback(config: Config): Promise<AppUpdateInfo> {
    try {
      const response = await fetch('/version.json');
      const versionData = await response.json();
      const currentVersion = versionData.bundleVersion || '1.0.0';
      return this.compareVersions(currentVersion, config);
    } catch {
      const currentVersion = '1.0.0';
      return this.compareVersions(currentVersion, config);
    }
  }

  async openStore(): Promise<void> {
    const config = readConfigFromCache();
    this.openStoreUrl(config || undefined);
  }

  private openStoreUrl(config?: Config): void {
    const platform = Capacitor.getPlatform();
    let storeUrl = '';

    if (platform === 'ios') {
      storeUrl = config?.appStoreUrl || 'https://apps.apple.com/app/id[YOUR_APP_ID]';
    } else if (platform === 'android') {
      storeUrl = config?.playStoreUrl || 'https://play.google.com/store/apps/details?id=com.nusrif.ecom';
    }

    if (storeUrl) {
      if (typeof window !== 'undefined') {
        window.open(storeUrl, '_blank');
      }
    }
  }

  private compareVersions(currentVersion: string, config: Config): AppUpdateInfo {
    const hasUpdate = this.isVersionNewer(config.latestVersion, currentVersion);
    const isForceUpdate = this.isVersionNewer(config.minSupportedVersion, currentVersion);

    return {
      currentVersion,
      latestVersion: config.latestVersion,
      minSupportedVersion: config.minSupportedVersion,
      forceUpdate: config.forceUpdate || isForceUpdate,
      hasUpdate,
      isForceUpdate,
    };
  }

  private isVersionNewer(version1: string, version2: string): boolean {
    const v1Parts = version1.split('.').map(Number);
    const v2Parts = version2.split('.').map(Number);

    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
      const v1 = v1Parts[i] || 0;
      const v2 = v2Parts[i] || 0;

      if (v1 > v2) return true;
      if (v1 < v2) return false;
    }

    return false;
  }
}

class WebAppUpdateService implements AppUpdateService {
  async checkForUpdates(): Promise<AppUpdateInfo> {
    const config = readConfigFromCache();
    if (!config) throw new Error('Config not available');

    // For web, we'll use the bundle version from the version.json file
    try {
      const response = await fetch('/version.json');
      const versionData = await response.json();
      const currentVersion = versionData.bundleVersion || '1.0.0';

      return this.compareVersions(currentVersion, config);
    } catch (error) {
      console.error('Failed to get web version:', error);
      // Fallback to package.json version
      const currentVersion = '1.0.0';
      return this.compareVersions(currentVersion, config);
    }
  }

  async openStore(): Promise<void> {
    const config = readConfigFromCache();
    // For web, open respective store URL in a new tab if available
    const platform = Capacitor.getPlatform();
    const targetUrl = platform === 'ios' ? config?.appStoreUrl : config?.playStoreUrl;
    if (targetUrl && typeof window !== 'undefined') {
      window.open(targetUrl, '_blank');
    } else {
      console.log('Store opening not supported on web platform or URL not configured');
    }
  }

  private compareVersions(currentVersion: string, config: Config): AppUpdateInfo {
    const hasUpdate = this.isVersionNewer(config.latestVersion, currentVersion);
    const isForceUpdate = this.isVersionNewer(config.minSupportedVersion, currentVersion);

    return {
      currentVersion,
      latestVersion: config.latestVersion,
      minSupportedVersion: config.minSupportedVersion,
      forceUpdate: config.forceUpdate || isForceUpdate,
      hasUpdate,
      isForceUpdate,
    };
  }

  private isVersionNewer(version1: string, version2: string): boolean {
    const v1Parts = version1.split('.').map(Number);
    const v2Parts = version2.split('.').map(Number);

    for (let i = 0; i < Math.max(v1Parts.length, v2Parts.length); i++) {
      const v1 = v1Parts[i] || 0;
      const v2 = v2Parts[i] || 0;

      if (v1 > v2) return true;
      if (v1 < v2) return false;
    }

    return false;
  }
}

// Factory function to get the appropriate service
export function createAppUpdateService(): AppUpdateService {
  if (Capacitor.isNativePlatform()) {
    return new NativeAppUpdateService();
  } else {
    return new WebAppUpdateService();
  }
}

// Export the service instance
export const appUpdateService = createAppUpdateService(); 